<template>
  <div class="user-edit">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <i class="bi bi-pencil me-2"></i>
            Editar Usuario
          </h1>
          <p class="page-description">
            Modificar información del usuario
          </p>
        </div>
        <div class="actions">
          <router-link to="/users" class="btn btn-outline-secondary">
            <i class="bi bi-arrow-left me-2"></i>
            Volver
          </router-link>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-2 text-muted">Cargando datos del usuario...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger">
      <h5 class="alert-heading">
        <i class="bi bi-exclamation-triangle me-2"></i>
        Error al cargar el usuario
      </h5>
      <p>{{ error }}</p>
      <hr>
      <router-link to="/users" class="btn btn-sm btn-outline-danger">
        <i class="bi bi-arrow-left me-1"></i>
        Volver a la lista
      </router-link>
    </div>

    <!-- Form Card -->
    <div v-else class="card">
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <!-- Basic Information -->
          <div class="form-section mb-4">
            <h5 class="form-section-title">
              <i class="bi bi-info-circle me-2"></i>
              Información Básica
            </h5>

            <div class="row g-3">
              <div class="col-md-6">
                <label for="fullname" class="form-label">
                  Nombre Completo
                </label>
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.fullname }"
                  id="fullname"
                  v-model="formData.fullname"
                  placeholder="Ej: Juan Pérez"
                >
                <div class="invalid-feedback" v-if="errors.fullname">
                  {{ errors.fullname }}
                </div>
              </div>

              <div class="col-md-6">
                <label for="email" class="form-label">
                  Email <span class="text-danger">*</span>
                </label>
                <input
                  type="email"
                  class="form-control"
                  :class="{ 'is-invalid': errors.email }"
                  id="email"
                  v-model="formData.email"
                  placeholder="usuario@ejemplo.com"
                  required
                >
                <div class="invalid-feedback" v-if="errors.email">
                  {{ errors.email }}
                </div>
              </div>
            </div>
          </div>

          <!-- Password Change (Optional) -->
          <div class="form-section mb-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="form-section-title mb-0">
                <i class="bi bi-key me-2"></i>
                Cambiar Contraseña
              </h5>
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="changePassword"
                  v-model="changePassword"
                >
                <label class="form-check-label" for="changePassword">
                  Modificar contraseña
                </label>
              </div>
            </div>

            <div v-if="changePassword" class="row g-3">
              <div class="col-md-6">
                <label for="password" class="form-label">
                  Nueva Contraseña <span class="text-danger">*</span>
                </label>
                <div class="input-group">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': errors.password }"
                    id="password"
                    v-model="formData.password"
                    placeholder="••••••••"
                    :required="changePassword"
                    minlength="8"
                  >
                  <button
                    class="btn btn-outline-secondary"
                    type="button"
                    @click="showPassword = !showPassword"
                  >
                    <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                  </button>
                </div>
                <div class="invalid-feedback d-block" v-if="errors.password">
                  {{ errors.password }}
                </div>
                <small class="form-text text-muted">
                  Mínimo 8 caracteres
                </small>
              </div>

              <div class="col-md-6">
                <label for="confirmPassword" class="form-label">
                  Confirmar Contraseña <span class="text-danger">*</span>
                </label>
                <div class="input-group">
                  <input
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': errors.confirmPassword }"
                    id="confirmPassword"
                    v-model="confirmPassword"
                    placeholder="••••••••"
                    :required="changePassword"
                    minlength="8"
                  >
                  <button
                    class="btn btn-outline-secondary"
                    type="button"
                    @click="showConfirmPassword = !showConfirmPassword"
                  >
                    <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                  </button>
                </div>
                <div class="invalid-feedback d-block" v-if="errors.confirmPassword">
                  {{ errors.confirmPassword }}
                </div>
              </div>

              <div class="col-12">
                <div class="alert alert-warning">
                  <i class="bi bi-exclamation-triangle me-2"></i>
                  <strong>Advertencia:</strong> Cambiar la contraseña invalidará el token API del usuario.
                </div>
              </div>
            </div>

            <div v-else class="text-muted">
              <i class="bi bi-info-circle me-2"></i>
              La contraseña se mantendrá sin cambios
            </div>
          </div>

          <!-- Role and Client Assignment -->
          <div class="form-section mb-4">
            <h5 class="form-section-title">
              <i class="bi bi-diagram-3 me-2"></i>
              Asignaciones
            </h5>

            <div class="row g-3">
              <div class="col-md-6">
                <label for="rol_id" class="form-label">
                  Rol
                </label>
                <select
                  class="form-select"
                  :class="{ 'is-invalid': errors.rol_id }"
                  id="rol_id"
                  v-model="formData.rol_id"
                >
                  <option :value="null">Sin rol asignado</option>
                  <option v-for="role in availableRoles" :key="role.id" :value="role.id">
                    {{ role.name }}
                  </option>
                </select>
                <div class="invalid-feedback" v-if="errors.rol_id">
                  {{ errors.rol_id }}
                </div>
              </div>

              <div class="col-md-6">
                <label for="client_id" class="form-label">
                  Cliente
                </label>
                <select
                  class="form-select"
                  :class="{ 'is-invalid': errors.client_id }"
                  id="client_id"
                  v-model="formData.client_id"
                >
                  <option :value="null">Sin cliente asignado</option>
                  <option v-for="client in availableClients" :key="client.id" :value="client.id">
                    {{ client.nombre }} ({{ client.cuit }})
                  </option>
                </select>
                <div class="invalid-feedback" v-if="errors.client_id">
                  {{ errors.client_id }}
                </div>
              </div>
            </div>
          </div>

          <!-- API Token Management -->
          <div class="form-section mb-4">
            <h5 class="form-section-title">
              <i class="bi bi-key-fill me-2"></i>
              Gestión de Token API
            </h5>

            <div v-if="userStore.currentUser?.is_token_valid" class="alert alert-success">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <i class="bi bi-check-circle me-2"></i>
                  <strong>Token API Activo</strong>
                  <div class="small mt-1">
                    Expira: {{ formatDate(userStore.currentUser.token_api_expires) }}
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="handleRevokeToken"
                  :disabled="userStore.loading"
                >
                  <i class="bi bi-x-circle me-1"></i>
                  Revocar Token
                </button>
              </div>
            </div>

            <div v-else class="alert alert-info">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <i class="bi bi-info-circle me-2"></i>
                  <strong>Sin Token API</strong>
                  <div class="small mt-1">
                    El usuario no tiene un token API generado
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  @click="handleGenerateToken"
                  :disabled="userStore.loading"
                >
                  <i class="bi bi-plus-circle me-1"></i>
                  Generar Token
                </button>
              </div>
            </div>
          </div>

          <!-- User Info -->
          <div class="form-section mb-4">
            <h5 class="form-section-title">
              <i class="bi bi-clock-history me-2"></i>
              Información del Sistema
            </h5>
            <div class="row g-3">
              <div class="col-md-4">
                <strong>ID:</strong> {{ userStore.currentUser?.id }}
              </div>
              <div class="col-md-4">
                <strong>Creado:</strong> {{ formatDate(userStore.currentUser?.created_at) }}
              </div>
              <div class="col-md-4">
                <strong>Última actualización:</strong> {{ formatDate(userStore.currentUser?.updated_at) }}
              </div>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="form-actions">
            <router-link to="/users" class="btn btn-secondary">
              <i class="bi bi-x-lg me-2"></i>
              Cancelar
            </router-link>
            <button type="submit" class="btn btn-primary" :disabled="userStore.loading">
              <span v-if="userStore.loading">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Guardando...
              </span>
              <span v-else>
                <i class="bi bi-check-lg me-2"></i>
                Guardar Cambios
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '../../stores/users'
import { useRoleStore } from '../../stores/roles'
import { useClientStore } from '../../stores/clients'

// Router and Stores
const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const roleStore = useRoleStore()
const clientStore = useClientStore()

// Props
const userId = parseInt(route.params.id)

// State
const loading = ref(true)
const error = ref(null)

// Form data
const formData = reactive({
  fullname: '',
  email: '',
  password: '',
  rol_id: null,
  client_id: null
})

const changePassword = ref(false)
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const errors = ref({})

// Available options
const availableRoles = ref([])
const availableClients = ref([])

// Methods
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const validateForm = () => {
  errors.value = {}

  // Validate email
  if (!formData.email) {
    errors.value.email = 'El email es requerido'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.value.email = 'El email no es válido'
  }

  // Validate password if changing
  if (changePassword.value) {
    if (!formData.password) {
      errors.value.password = 'La contraseña es requerida'
    } else if (formData.password.length < 8) {
      errors.value.password = 'La contraseña debe tener al menos 8 caracteres'
    }

    if (!confirmPassword.value) {
      errors.value.confirmPassword = 'Debe confirmar la contraseña'
    } else if (formData.password !== confirmPassword.value) {
      errors.value.confirmPassword = 'Las contraseñas no coinciden'
    }
  }

  return Object.keys(errors.value).length === 0
}

const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  try {
    const userData = {
      fullname: formData.fullname || undefined,
      email: formData.email,
      rol_id: formData.rol_id || undefined,
      client_id: formData.client_id || undefined
    }

    // Only include password if changing
    if (changePassword.value) {
      userData.password = formData.password
    }

    await userStore.updateUser(userId, userData)
    router.push('/users')
  } catch (error) {
    console.error('Error updating user:', error)
    // Errors are handled by the store
  }
}

const handleGenerateToken = async () => {
  try {
    await userStore.generateToken(userId, 30)
  } catch (error) {
    console.error('Error generating token:', error)
  }
}

const handleRevokeToken = async () => {
  try {
    await userStore.revokeToken(userId)
  } catch (error) {
    console.error('Error revoking token:', error)
  }
}

// Lifecycle
onMounted(async () => {
  try {
    loading.value = true

    // Load user data
    const user = await userStore.fetchUser(userId, true)

    // Populate form
    formData.fullname = user.fullname || ''
    formData.email = user.email
    formData.rol_id = user.rol_id
    formData.client_id = user.client_id

    // Load roles (active only)
    await roleStore.fetchRoles(1, 100)
    availableRoles.value = roleStore.roles.filter(role => !role.is_deleted)

    // Load clients (active only)
    await clientStore.fetchClients({ page: 1, size: 100 })
    availableClients.value = clientStore.clients.filter(client => !client.deleted_at)

    loading.value = false
  } catch (err) {
    console.error('Error loading user:', err)
    error.value = err.message || 'Error al cargar el usuario'
    loading.value = false
  }
})
</script>

<style scoped>
.user-edit {
  padding: 0;
}

.page-header {
  background: var(--card-bg);
  border-bottom: 1px solid var(--border-color);
  padding: 1.5rem 0;
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.title-section h1 {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
}

.title-section p {
  margin: 0.25rem 0 0;
  color: var(--text-secondary);
}

.card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.form-section {
  padding: 1.5rem;
  background: var(--section-bg);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.form-section-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--text-primary);
  display: flex;
  align-items: center;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
  margin-top: 1.5rem;
}

.form-label {
  font-weight: 500;
  color: var(--text-primary);
}

.form-text {
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .form-actions .btn {
    width: 100%;
  }
}
</style>
